// El carrusel de plantillas de la portada de Redes — ADR-2026-10-03-redes-carrusel-de-plantillas.
//
// Un abanico tipo acordeón entre el título y la caja, sin nombres: la plantilla del medio grande y al frente,
// dos a cada lado más chicas, giradas y por detrás; cada pocos segundos todo corre un
// lugar y entra la siguiente. Muestra lo que se puede lograr antes de escribir nada.
//
// La lista es FIJA y elegida: las seis que pidió Matías (Rocas al mar —en la base no
// existe "Rocas de mar"—, Color block, Living retro lila, Colinas al viento, Sneaker en
// mano, Marco en el jardín) intercaladas con las más usadas de los últimos 60 días por
// cantidad de empresas, sin cuentas de prueba (consulta del 2026-10-03 sobre
// `imagenes_generadas.datos_generacion->>'template_id'`). Las imágenes viven en el bucket
// público `templates`, así que se ven para cualquier rubro sin cargar el catálogo.
//
// Arranca a los 0,7 s y después corre cada 2,4 s; tocar la mitad izquierda retrocede y la
// derecha avanza. Se pausa al pasar el mouse y no se mueve solo con `prefers-reduced-motion`.
//
// Con el medio en Video (ADR-2026-10-04-redes-carrusel-de-videos) el abanico pasa a los
// clips de las plantillas de video —los tipos del Creador, los mismos de la grilla de
// abajo— en cards 9:16. Sólo las cinco a la vista montan su `<video>`; las que esperan
// afuera muestran el poster, así no se bajan ocho clips a la vez.
//
// En el teléfono (`compacto`) se ven tres en vez de cinco, más chicas: la del medio y una a
// cada lado (ADR-2026-10-09-asistente-en-crear-6).
//
// Al cambiar de medio (ADR-2026-10-04-redes-carrusel-cambio-de-medio) el abanico que se va
// se pliega hacia el centro mientras se apaga y el que llega se abre desde ahí, de a una
// card por vez desde la del medio. Las dos capas conviven un instante, una sobre la otra.

import React, { useEffect, useRef, useState } from 'react';
import { templatePreviewUrl } from '../../templatesApi';
import { useVideoTipos } from '../../videoTiposCatalog';
import type { Medio } from './redes';
import { IMAGENES_DEL_ABANICO, VIDEOS_DEL_ABANICO, type RubroDeCrear } from './contenidoPorRubro';
import { miniatura } from '../../../../utils/miniatura';

interface PlantillaDelCarrusel {
  nombre: string;
  path: string;
}

/** Una card del abanico, de imagen o de video. */
interface CardDelCarrusel {
  clave: string;
  imagen: string;
  video?: string;
}

const PLANTILLAS: PlantillaDelCarrusel[] = [
  { nombre: 'Rocas al mar', path: 'globales/af10cc0d-1987-4748-b057-08c6728b3ef4_opt_1781992517578.webp' },
  { nombre: 'Sombra de árbol', path: 'globales/cff24b8b-9b1d-4708-9de9-4d40a52b4be9_opt_1781991767436.webp' },
  { nombre: 'Color block', path: 'globales/2d89639c-82f7-4046-aedf-864229046af2_opt_1781990819478.webp' },
  { nombre: 'Parque al sol', path: 'globales/a2502beb-5f93-4037-8c93-f050093ecace_opt_1781991920574.webp' },
  { nombre: 'Living retro lila', path: 'globales/99fc507c-a79c-4db0-8089-925fac75340e_opt_1782759821234.webp' },
  { nombre: 'Cala turquesa', path: 'globales/d3d994ae-5c02-46c7-867f-ca428689357d_opt_1782186256047.webp' },
  { nombre: 'Colinas al viento', path: 'globales/0f019836-9773-4ec1-9af7-af6f55eed3f8_opt_1781042242719.webp' },
  { nombre: 'Aparador retro', path: 'globales/1045b488-127d-46e2-8071-5a6bf07f2418_opt_1781991880810.webp' },
  { nombre: 'Sneaker en mano', path: 'globales/fc101b71-1322-4c1f-91ea-96d0ef4d366b_opt_1782236851994.webp' },
  { nombre: 'Velero al atardecer', path: 'globales/8a468911-ab5e-4c7b-9ee0-07517d5b01d1_opt_1781992491500.webp' },
  { nombre: 'Marco en el jardín', path: 'globales/12a72f85-d0a0-4ad5-b8fa-7a593efbb1a9_opt_1782759971111.webp' },
  { nombre: 'Cruce urbano', path: 'globales/d3796319-72bf-4a39-9b66-59942b36da85_opt_1781990625763.webp' },
];

const CARDS_DE_IMAGEN: CardDelCarrusel[] = PLANTILLAS.map((p) => ({ clave: p.path, imagen: templatePreviewUrl(p.path) }));

/** Cada cuánto corre un lugar (ms). */
const INTERVALO_MS = 2400;
/** El primer paso llega antes: que se note enseguida que se mueve. */
const PRIMER_PASO_MS = 700;
/** Lo que tarda el abanico que se va en plegarse; después se desmonta. */
const SALIDA_MS = 380;
/** El que llega espera un poco a que el otro empiece a irse, y abre de a una por vez. */
const ENTRADA_DEMORA_MS = 120;
const ENTRADA_ESCALON_MS = 50;
/** Medidas de la del medio (px) y cuánto se separa cada posición del centro; las demás
 *  salen de acá por escala. Video va en 9:16, más angosta y con el paso más corto para
 *  que se solapen igual que las de imagen. */
const MEDIDAS: Record<Medio, { ancho: number; alto: number; paso: number }> = {
  imagen: { ancho: 168, alto: 210, paso: 118 },
  video: { ancho: 128, alto: 228, paso: 92 },
};
/** Las del teléfono: tres a la vista en ~360px de ancho. */
const MEDIDAS_COMPACTAS: Record<Medio, { ancho: number; alto: number; paso: number }> = {
  imagen: { ancho: 136, alto: 170, paso: 98 },
  video: { ancho: 100, alto: 178, paso: 76 },
};

/** La posición de cada card respecto del centro: hasta `alcance` se ven, el resto espera afuera. */
function posicion(i: number, actual: number, total: number): number {
  let d = (i - actual) % total;
  if (d > total / 2) d -= total;
  if (d < -total / 2) d += total;
  return d;
}

/**
 * Un abanico de un medio. `entrando`: arranca plegado en el centro y se abre. `saliendo`:
 * se pliega al centro, se apaga y deja de responder (la capa nueva queda arriba).
 */
const Abanico: React.FC<{ medio: Medio; entrando: boolean; saliendo: boolean; compacto: boolean; rubro: RubroDeCrear }> = ({
  medio,
  entrando,
  saliendo,
  compacto,
  rubro,
}) => {
  const { tipos } = useVideoTipos();
  // Belleza, Hogar y Gastronomía ven sus productos (ADR-2026-10-12-la-portada-de-crear-sigue-al-rubro);
  // Moda (y el resto) las plantillas globales y los clips de los tipos de video.
  const cards: CardDelCarrusel[] =
    medio === 'video'
      ? rubro !== 'moda'
        ? VIDEOS_DEL_ABANICO[rubro].map((c) => ({ clave: c.video, imagen: c.poster, video: c.video }))
        : tipos.filter((t) => t.videoUrl).map((t) => ({ clave: t.slug, imagen: t.posterUrl ?? '', video: t.videoUrl }))
      : rubro !== 'moda'
        ? // La copia de 420 px: la card mide 136–168 px y el original pesa 200–400 KB
          // (ADR-2026-10-18-las-fichas-de-la-home-y-crear-usan-miniaturas).
          IMAGENES_DEL_ABANICO[rubro].map((foto) => ({ clave: foto, imagen: miniatura(foto) }))
        : CARDS_DE_IMAGEN;
  const total = cards.length;
  const { ancho, alto, paso } = (compacto ? MEDIDAS_COMPACTAS : MEDIDAS)[medio];
  /** Cuántas se ven a cada lado de la del medio. */
  const alcance = compacto ? 1 : 2;
  const [actual, setActual] = useState(0);
  const [pausado, setPausado] = useState(false);
  const [reducido, setReducido] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const alCambiar = () => setReducido(mql.matches);
    alCambiar();
    mql.addEventListener('change', alCambiar);
    return () => mql.removeEventListener('change', alCambiar);
  }, []);

  // Un timeout por paso (y no un intervalo): cada cambio —automático o tocado— reinicia la
  // cuenta, así después de tocar no salta enseguida otro lugar.
  const [primero, setPrimero] = useState(true);
  useEffect(() => {
    if (pausado || reducido || saliendo || total < 2) return;
    const t = window.setTimeout(() => {
      setPrimero(false);
      setActual((a) => (a + 1) % total);
    }, primero && !entrando ? PRIMER_PASO_MS : INTERVALO_MS);
    return () => window.clearTimeout(t);
  }, [actual, pausado, reducido, saliendo, entrando, primero, total]);

  // El que llega pinta un cuadro plegado y recién en el siguiente se abre: si no, el
  // navegador nunca ve el estado de partida y no hay transición.
  const [desplegado, setDesplegado] = useState(!entrando);
  useEffect(() => {
    if (desplegado) return;
    let dos = 0;
    const uno = window.requestAnimationFrame(() => {
      dos = window.requestAnimationFrame(() => setDesplegado(true));
    });
    return () => {
      window.cancelAnimationFrame(uno);
      window.cancelAnimationFrame(dos);
    };
  }, [desplegado]);
  const plegado = saliendo || !desplegado;

  const mover = (sentido: 1 | -1) => {
    if (total < 2) return;
    setPrimero(false);
    setActual((a) => (a + sentido + total) % total);
  };

  return (
    <div
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      className={`absolute inset-0 ${saliendo ? 'pointer-events-none' : ''}`}
    >
      {/* Tocar la mitad izquierda retrocede; la derecha avanza. Dos botones a lo alto,
          encima de las cards, sin cursor propio: el `button` ya toma la flecha de Atlas (index.css). */}
      <button
        type="button"
        aria-label="Plantilla anterior"
        onClick={() => mover(-1)}
        className="absolute inset-y-0 left-0 z-20 w-1/2"
      />
      <button
        type="button"
        aria-label="Plantilla siguiente"
        onClick={() => mover(1)}
        className="absolute inset-y-0 right-0 z-20 w-1/2"
      />
      {cards.map((p, i) => {
        const d = posicion(i, actual, total);
        const lejos = Math.abs(d);
        const visible = lejos <= alcance;
        // Afuera (|d| > alcance) esperan del lado por el que van a entrar, sin verse.
        const x = Math.max(-(alcance + 1), Math.min(alcance + 1, d)) * paso;
        const escala = lejos === 0 ? 1 : lejos === 1 ? 0.8 : 0.64;
        const giro = Math.max(-alcance, Math.min(alcance, d)) * 5;
        // Plegada: todas juntas en el centro, un poco más chicas y sin verse. Con
        // `prefers-reduced-motion` no viajan: sólo se apagan y se encienden.
        const transform =
          plegado && !reducido
            ? `translateX(0px) scale(${escala * 0.86}) rotate(0deg)`
            : `translateX(${x}px) scale(${escala}) rotate(${giro}deg)`;
        const transition = saliendo
          ? `transform ${SALIDA_MS}ms cubic-bezier(0.4, 0, 1, 1), opacity ${SALIDA_MS}ms ease, box-shadow ${SALIDA_MS}ms ease`
          : 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms ease, box-shadow 700ms ease';
        // La demora sólo en la apertura: después, el paso del carrusel corre sin esperar.
        const demora = entrando && !saliendo && actual === 0 && primero ? ENTRADA_DEMORA_MS + lejos * ENTRADA_ESCALON_MS : 0;
        return (
          <span
            key={p.clave}
            aria-hidden="true"
            className="absolute left-1/2 top-1/2 block overflow-hidden rounded-2xl bg-[#EEF0F5] ring-1 ring-black/[0.06]"
            style={{
              width: ancho,
              height: alto,
              marginLeft: -ancho / 2,
              marginTop: -alto / 2,
              zIndex: 10 - lejos,
              opacity: plegado || !visible ? 0 : lejos === 2 ? 0.7 : 1,
              transform,
              boxShadow: lejos === 0 ? '0 22px 50px -18px rgb(var(--brand-black-rgb) / 0.45)' : '0 10px 26px -14px rgb(var(--brand-black-rgb) / 0.3)',
              transition,
              transitionDelay: `${demora}ms`,
            }}
          >
            {p.video && visible ? (
              <video
                src={p.video}
                poster={p.imagen || undefined}
                autoPlay={!reducido}
                loop
                muted
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
              />
            ) : p.imagen ? (
              <img src={p.imagen} alt="" loading="lazy" className="h-full w-full object-cover" />
            ) : null}
          </span>
        );
      })}
    </div>
  );
};

interface Capa {
  id: number;
  medio: Medio;
  entrando: boolean;
  saliendo: boolean;
}

export const CarruselDePlantillas: React.FC<{ medio?: Medio; compacto?: boolean; rubro?: RubroDeCrear; className?: string }> = ({
  medio = 'imagen',
  compacto = false,
  rubro = 'moda',
  className = '',
}) => {
  // Cada cambio de medio suma una capa nueva y marca las demás como salientes; éstas se
  // desmontan al terminar de plegarse. El `id` (y no el medio) es la key: ir y volver
  // rápido deja dos capas del mismo medio a la vez sin chocar.
  const siguienteId = useRef(1);
  const [capas, setCapas] = useState<Capa[]>([{ id: 0, medio, entrando: false, saliendo: false }]);
  const ultimoMedio = useRef(medio);
  const timeouts = useRef<number[]>([]);

  useEffect(() => {
    if (ultimoMedio.current === medio) return;
    ultimoMedio.current = medio;
    const id = siguienteId.current++;
    setCapas((cs) => [...cs.map((c) => ({ ...c, saliendo: true })), { id, medio, entrando: true, saliendo: false }]);
    timeouts.current.push(
      window.setTimeout(() => setCapas((cs) => cs.filter((c) => !c.saliendo || c.id >= id)), SALIDA_MS + 40),
    );
  }, [medio]);

  useEffect(() => () => timeouts.current.forEach((t) => window.clearTimeout(t)), []);

  return (
    <div className={`relative mx-auto w-full max-w-[640px] ${compacto ? 'h-[190px]' : 'h-[230px]'} ${className}`}>
      {capas.map((c) => (
        // El rubro en la key: si llega después (se lee de la marca), el abanico arranca de cero.
        <Abanico key={`${c.id}-${rubro}`} medio={c.medio} entrando={c.entrando} saliendo={c.saliendo} compacto={compacto} rubro={rubro} />
      ))}
    </div>
  );
};

export default CarruselDePlantillas;
